import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from "react";

type SubStepInfo = {
  steps: string[];
  currentStep: string;
  canGoNext: boolean;
  canGoPrev: boolean;
};

type SubStepHandlers = {
  goNext: () => boolean; // Returns true if handled, false if should go to next main step
  goPrev: () => boolean; // Returns true if handled, false if should go to prev main step
};

type SubStepContextValue = {
  subStepInfo: SubStepInfo | null;
  handlers: SubStepHandlers | null;
  register: (info: SubStepInfo, handlers: SubStepHandlers) => void;
  unregister: () => void;
};

const SubStepContext = createContext<SubStepContextValue | null>(null);

export function SubStepProvider({ children }: { children: React.ReactNode }) {
  const [subStepInfo, setSubStepInfo] = useState<SubStepInfo | null>(null);
  const [handlers, setHandlers] = useState<SubStepHandlers | null>(null);

  const register = useCallback((info: SubStepInfo, h: SubStepHandlers) => {
    setSubStepInfo(info);
    setHandlers(() => h);
  }, []);

  const unregister = useCallback(() => {
    setSubStepInfo(null);
    setHandlers(null);
  }, []);

  const value = useMemo(() => ({
    subStepInfo,
    handlers,
    register,
    unregister,
  }), [subStepInfo, handlers, register, unregister]);

  return (
    <SubStepContext.Provider value={value}>
      {children}
    </SubStepContext.Provider>
  );
}

export function useSubStep() {
  const ctx = useContext(SubStepContext);
  if (!ctx) {
    throw new Error("useSubStep must be used within SubStepProvider");
  }
  return ctx;
}

/**
 * Hook for step components to register their sub-steps
 */
export function useRegisterSubSteps(
  steps: string[],
  currentStep: string,
  setCurrentStep: (step: string) => void,
  canAdvance: (step: string) => boolean
) {
  const { register, unregister } = useSubStep();

  // Memoize steps array by content to prevent unnecessary effect runs
  const stepsKey = steps.join(",");
  const stableSteps = useMemo(() => steps, [stepsKey]);

  // Use ref for setCurrentStep to avoid it being a dependency
  const setCurrentStepRef = useRef(setCurrentStep);
  setCurrentStepRef.current = setCurrentStep;

  // Use ref for canAdvance to always have latest version
  const canAdvanceRef = useRef(canAdvance);
  canAdvanceRef.current = canAdvance;

  React.useEffect(() => {
    const currentIndex = stableSteps.indexOf(currentStep);
    const isFirstSubStep = currentIndex === 0;
    const isLastSubStep = currentIndex === stableSteps.length - 1;

    const info: SubStepInfo = {
      steps: stableSteps,
      currentStep,
      canGoNext: !isLastSubStep || canAdvanceRef.current(currentStep),
      canGoPrev: !isFirstSubStep,
    };

    const handlers: SubStepHandlers = {
      goNext: () => {
        if (isLastSubStep) {
          // At last sub-step - let main navigation handle it (validation will run)
          return false;
        }

        // Check if current step is complete before advancing
        if (!canAdvanceRef.current(currentStep)) {
          // Can't advance - return false so validation runs and shows error
          return false;
        }

        // Go to next sub-step
        const nextStep = stableSteps[currentIndex + 1];
        if (nextStep) {
          setCurrentStepRef.current(nextStep);
          return true;
        }
        return false;
      },
      goPrev: () => {
        if (isFirstSubStep) {
          // At first sub-step - let main navigation handle it
          return false;
        }
        // Go to previous sub-step
        const prevStep = stableSteps[currentIndex - 1];
        if (prevStep) {
          setCurrentStepRef.current(prevStep);
          return true;
        }
        return false;
      },
    };

    register(info, handlers);

    return () => unregister();
  }, [stableSteps, currentStep, register, unregister]);
}
